import React, {useEffect, useMemo, useState} from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import {useCookies} from 'react-cookie'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'

import {useCustomerType} from '@salesforce/commerce-sdk-react'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {
    Box,
    Flex,
    IconButton,
    useMediaQuery,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

import {useNewsLetter} from '../../clients/contentful/hooks'
import NewsLetterModal from '../../components/newsletter-modal'
import {
    localStorageKeys,
    NEWS_LETTER_OPEN_DELAY,
    styleConfigChakraKeys
} from '../../constants'
import {LanguageSelector} from '../language-selector/LanguageSelector'
import {getCookieBotId} from '../../utils/site-utils'
import {BrandLogo} from '../icons'
import Link from '../link'
import BurgerAnimation from './partials/burger-animation'
import LoginAction from './partials/login'
import SearchAction from './partials/search'
import CartAction from './partials/cart'

const onClient = typeof window !== 'undefined'

const Header = ({
    children,
    onMenuClick = noop,
    onLogoClick = noop,
    mobileNavOpen,
    enableBurgerAnimation,
    header,
    activeAnnouncementBar,
    ...props
}) => {
    const intl = useIntl()
    const location = useLocation()
    const {isRegistered} = useCustomerType()
    const [showLoading, setShowLoading] = useState(false)
    const [isDesktop] = useMediaQuery('(min-width: 992px)')
    const styles = useMultiStyleConfig(styleConfigChakraKeys.header)

    /* Menu links - top header */
    const topData = useMemo(() => {
        const topMenus = header?.top ?? []
        const topDesktop = topMenus?.find((item) => item?.desktop)?.desktop
        const topMobile = topMenus?.find((item) => item?.mobile)?.mobile

        return !isDesktop && topMobile ? topMobile : topDesktop
    }, [header, isDesktop])

    /* Menu links - list */
    const leftData = header?.left ?? []
    const rightData = header?.right ?? []

    const [isNewsletterDialogOpen, setIsNewsletterDialogOpen] = useState(false)
    const [isCookieDialogOpen, setIsCookieDialogOpen] = useState(false)
    const [isCookieBotLoadingComplete, setIsCookieBotLoadingComplete] =
        useState(false)
    const {data: newsLetter} = useNewsLetter()

    const [cookies, setCookie, removeCookie] = useCookies([
        'rennai_newsLetter',
        'rennai_hidden_announcements'
    ])

    const hasActiveAnnouncementBar = useMemo(() => {
        const slides = activeAnnouncementBar?.slides ?? []
        const items = (cookies?.rennai_hidden_announcements ?? '').split(',')
        const aSlides = slides?.filter(
            (item) => !items?.includes(item?.sys?.id)
        )
        return aSlides.length > 0
    }, [activeAnnouncementBar, cookies])

    useEffect(() => {
        if (location.pathname.includes('/newsletter')) {
            setIsNewsletterDialogOpen(false)
        } else if (isCookieBotLoadingComplete && !isCookieDialogOpen) {
            setTimeout(() => {
                const alreadySubscribed = localStorage.getItem(
                    localStorageKeys.newsLetterSubscription
                )
                if (alreadySubscribed && alreadySubscribed === 'true') {
                    return
                }

                if (cookies.rennai_newsLetter) {
                    return
                }
                const in30Days = new Date(
                    new Date().getTime() + 30 * 24 * 60 * 60 * 1000
                )
                setIsNewsletterDialogOpen(true)
                setCookie('rennai_newsLetter', true, {expires: in30Days})
            }, [NEWS_LETTER_OPEN_DELAY])
        } else if (isCookieBotLoadingComplete && isCookieDialogOpen) {
            setTimeout(() => {
                removeCookie('rennai_newsLetter')
                setIsNewsletterDialogOpen(true)
            }, [NEWS_LETTER_OPEN_DELAY])
        }
    }, [isCookieDialogOpen, isCookieBotLoadingComplete, location])

    useEffect(() => {
        const onCookieDisplay = () => {
            setIsCookieDialogOpen(true)
        }

        const onDialogClose = () => {
            setIsCookieDialogOpen(false)
        }

        const onScriptLoad = () => {
            setIsCookieBotLoadingComplete(true)
        }
        if (onClient) {
            const culture = intl?.locale?.split('-')[0] ?? 'EN'
            const script = document.createElement('script')

            window.addEventListener('CookiebotOnDialogDisplay', onCookieDisplay)
            window.addEventListener('CookiebotOnLoad', onScriptLoad)
            window.addEventListener('CookiebotOnDecline', onDialogClose)
            window.addEventListener('CookiebotOnAccept', onDialogClose)

            // Set the script attributes
            script.src = 'https://consent.cookiebot.com/uc.js'
            script.id = 'Cookiebot'
            script.setAttribute('data-cbid', getCookieBotId()) // Ensure this is your actual Cookiebot ID
            script.setAttribute('data-culture', culture.toUpperCase())
            // script.setAttribute('data-blockingmode', 'auto')
            script.type = 'text/javascript'
            script.async = true

            // Append the script to the document body
            document.body.appendChild(script)

            return () => {
                // Clean up script and event listener
                document.body.removeChild(script)
                window.removeEventListener(
                    'CookiebotOnDialogDisplay',
                    onCookieDisplay
                )
                window.removeEventListener('CookiebotOnLoad', onScriptLoad)

                window.removeEventListener('CookiebotOnDecline', onDialogClose)
                window.removeEventListener('CookiebotOnAccept', onDialogClose)
            }
        }
    }, [])

    const LinkActions = {
        Account: (item) => (
            <LoginAction
                accountBtn={item}
                isRegistered={isRegistered}
                setShowLoading={setShowLoading}
                showLoading={showLoading}
                dropdownData={header?.account}
            />
        ),
        'Language Selector': () => <LanguageSelector />,
        'Book service': (item) => (
            <Link {...item} buttonStyles={styles.bookServicesBtn} />
        ),
        Search: (item) => (
            <SearchAction
                search={item}
                hasActiveAnnouncementBar={hasActiveAnnouncementBar}
            />
        ),
        Cart: (item) => <CartAction {...item} />,
        Link: (item) => <Link {...item} />,
        default: (item) => <Link {...item} />
    }

    if (showLoading) return <LoadingSpinner wrapperStyles={{height: '100vh'}} />

    return (
        <Flex
            {...styles.container}
            {..._.omit(props, ['onCloseMegaNavDrawer', 'onMyCartClick'])}
            gap={{base: 0, xl: '20px'}}
            flexFlow="column"
        >
            <Flex justifyContent="center" alignItems="center">
                <Box flex={1} display={{base: 'none', md: 'block'}}></Box>
                <Box
                    alignItems="center"
                    justifyContent="center"
                    marginLeft={{base: '10px', md: 'unset'}}
                >
                    <IconButton
                        aria-label={intl.formatMessage({
                            id: 'header.button.assistive_msg.logo',
                            defaultMessage: 'Logo'
                        })}
                        icon={<BrandLogo {...styles.logo} />}
                        {...styles.icons}
                        variant="unstyled"
                        onClick={onLogoClick}
                    />
                </Box>
                <Flex
                    flex={1}
                    flexFlow="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    gap="16px"
                >
                    {topData?.map((item) => {
                        const actionType = item?.actionType || 'default'
                        return LinkActions[actionType](item)
                    })}
                    <IconButton
                        aria-label={intl.formatMessage({
                            id: 'header.button.assistive_msg.menu',
                            defaultMessage: 'Menu'
                        })}
                        icon={
                            <BurgerAnimation
                                isActive={mobileNavOpen}
                                enableAnimation={enableBurgerAnimation}
                            />
                        }
                        variant="unstyled"
                        display={{xl: 'none'}}
                        {...styles.burger}
                        onClick={onMenuClick}
                        height="44px"
                        width="44px"
                    />
                </Flex>
            </Flex>
            <Flex>
                {isDesktop && (
                    <Flex
                        flex={1}
                        width="100%"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {leftData?.map((item) => {
                            const actionType = item?.actionType || 'default'
                            return LinkActions[actionType](item)
                        })}
                    </Flex>
                )}
                <Box {...styles.bodyContainer}>{children}</Box>
                {isDesktop && (
                    <Flex
                        flex={1}
                        width="100%"
                        alignItems="center"
                        justifyContent="flex-end"
                    >
                        {rightData?.map((item) => {
                            const actionType = item?.actionType || 'default'
                            return LinkActions[actionType](item)
                        })}
                    </Flex>
                )}
            </Flex>
            {newsLetter && !isCookieDialogOpen && (
                <NewsLetterModal
                    isOpen={isNewsletterDialogOpen}
                    data={newsLetter.modalCollection.items[0]}
                    onClose={() => setIsNewsletterDialogOpen(false)}
                />
            )}
        </Flex>
    )
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ]),
    mobileNavOpen: PropTypes.bool,
    enableBurgerAnimation: PropTypes.bool,
    header: PropTypes.object,
    activeAnnouncementBar: PropTypes.shape({
        slides: PropTypes.arrayOf(PropTypes.object)
    })
}

export default Header
